import { type FC, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type { InternalRoleFilters } from '@/types/internal'
import { Filter } from 'lucide-react'

interface InternalRolesFilterDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    currentFilters: InternalRoleFilters
    onApplyFilters: (filters: InternalRoleFilters) => void
}

export const InternalRolesFilterDialog: FC<InternalRolesFilterDialogProps> = ({
    open,
    onOpenChange,
    currentFilters,
    onApplyFilters,
}) => {
    const [filters, setFilters] = useState<InternalRoleFilters>(currentFilters)

    const handleApply = () => {
        onApplyFilters(filters)
        onOpenChange(false)
    }

    const handleReset = () => {
        const resetFilters: InternalRoleFilters = {
            page: 1,
            limit: 20,
        }
        setFilters(resetFilters)
        onApplyFilters(resetFilters)
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="rounded-full bg-blue-100 p-2">
                            <Filter className="h-4 w-4 text-blue-600" />
                        </div>
                        <DialogTitle>Filter Roles</DialogTitle>
                    </div>
                    <DialogDescription>
                        Apply filters to narrow down the role list
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Search */}
                    <div className="space-y-2">
                        <Label htmlFor="search">Search</Label>
                        <Input
                            id="search"
                            placeholder="Search by role ID or title..."
                            value={filters.search || ''}
                            onChange={e =>
                                setFilters({
                                    ...filters,
                                    search: e.target.value || undefined,
                                })
                            }
                        />
                    </div>

                    {/* Role Status */}
                    <div className="space-y-2">
                        <Label htmlFor="isActive">Role Status</Label>
                        <Select
                            value={
                                filters.isActive === undefined
                                    ? 'all'
                                    : String(filters.isActive)
                            }
                            onValueChange={(value: string) =>
                                setFilters({
                                    ...filters,
                                    isActive:
                                        value === 'all'
                                            ? undefined
                                            : value === 'true',
                                })
                            }
                        >
                            <SelectTrigger id="isActive">
                                <SelectValue placeholder="Select role status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="true">Active</SelectItem>
                                <SelectItem value="false">Inactive</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleReset}>
                        Reset Filters
                    </Button>
                    <Button onClick={handleApply}>Apply Filters</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
